<template>
    <PageWrapper :title="section ? section.name : 'Help'" :description="section ? '' : 'Guides and reference for the platform'">
        <div v-if="isLoading" class="KbSectionView__loading">
            Loading help articles…
        </div>
        <div v-else-if="hasError" class="KbSectionView__error">
            <p>We could not load help articles right now.</p>
            <NewButton size="sm" color="primary" @click="reload">
                Try again
            </NewButton>
        </div>
        <div v-else-if="!section" class="KbSectionView__notFound">
            This section could not be found. It may have been moved or removed.
        </div>
        <div v-else class="KbSectionView__content">
            <KbBreadcrumbs :sections="sectionPath.slice(0, -1)" :current="section.name" />
            <KbSectionTree :section="section" />
        </div>
    </PageWrapper>
</template>

<style lang="scss">
.KbSectionView__loading,
.KbSectionView__error,
.KbSectionView__notFound,
.KbSectionView__content {
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
}

.KbSectionView__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { KbSection } from '@evidentid/tprm-portal-lib/models/kb';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import KbBreadcrumbs from '@/modules/kb/components/KbBreadcrumbs/KbBreadcrumbs.vue';
    import KbSectionTree from '@/modules/kb/components/KbSectionTree/KbSectionTree.vue';
    import { useKbStore } from '@/modules/kb/store';
    import { findSectionPath } from '@/modules/kb/utils/sectionPath';

    export default defineComponent({
        name: 'KbSectionView',
        components: { KbBreadcrumbs, KbSectionTree, NewButton, PageWrapper },
        computed: {
            kbStore() {
                return useKbStore();
            },
            isLoading(): boolean {
                return [ OperationStatus.uninitialized, OperationStatus.loading ].includes(this.kbStore.treeStatus);
            },
            hasError(): boolean {
                return this.kbStore.treeStatus === OperationStatus.error;
            },
            // Sections nest (Webinar Videos sits under Release Notes), so the path is found by
            // walking the whole tree; its last element is the section this page shows.
            sectionPath(): KbSection[] {
                return findSectionPath(this.kbStore.tree, String(this.$route.params.sectionId));
            },
            section(): KbSection | null {
                return this.sectionPath[this.sectionPath.length - 1] ?? null;
            },
        },
        created(): void {
            this.kbStore.loadTree();
        },
        methods: {
            reload(): void {
                this.kbStore.loadTree({ force: true });
            },
        },
    });
</script>
